/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, MouseEvent, useCallback, useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { useBackdropClick } from "@adapptio/lib-editor-ui";
import {
	FC_ANCHOR,
	FC_DIRECTION,
	FC_POSITION,
	FC_SIZE,
	FloatingContainer,
	FloatingContainerContent
} from "../FloatingContainer";
import { FadeInOut } from "../FadeInOut";
import { IResourceNode, ResourceList, RESOURCE_LIST_STYLE } from "../ResourceList";

export interface IResourcePathBreadcrumbsPanelProps {
	/** Element to align panel to */
	targetRef: React.MutableRefObject<HTMLElement>;
	/** If the panel is opened */
	isOpened: boolean;
	/** Base path to filter resources */
	basePath: string;
	/** Currently selected resource ID */
	selectedId?: string;
	/** Called when a resource is selected */
	onSelect: (node: IResourceNode) => void;
	/** Called when panel is closed */
	onClose: () => void;
}

export const ResourcePathBreadcrumbsPanel: FC<IResourcePathBreadcrumbsPanelProps> = ({
	targetRef,
	isOpened,
	basePath,
	selectedId,
	onSelect,
	onClose
}) => {
	const panelRef = useRef<HTMLDivElement>();

	const preventClick = useCallback(
		(ev: MouseEvent) => {
			if (isOpened) {
				ev.stopPropagation();
			}
		},
		[ isOpened ]
	);

	useBackdropClick(
		(event) => {
			if (isOpened && isFunction(onClose)) {
				event.preventDefault();
				event.stopPropagation();
				onClose();
			}
		},
		[ panelRef, targetRef ],
		isOpened
	);

	const handleSelect = useCallback(
		(node: IResourceNode) => {
			onSelect(node);
		},
		[ onSelect ]
	);

	// Filter resources to only show those under the base path
	const isSelectable = useCallback(
		(node: IResourceNode) => {
			// All resources at the base path level are selectable
			return node.basePath === basePath;
		},
		[ basePath ]
	);

	return (
		<FadeInOut
			isVisible={isOpened}
			fadeOutDelay={250}
			render={(isVisible) => {
				const baseClass = BEM("edt-resource-path-breadcrumbs-panel", null, {
					"is-visible": isVisible
				});

				return (
					<FloatingContainer
						containerRef={panelRef}
						parent={targetRef}
						anchor={FC_ANCHOR.LEFT_BOTTOM}
						direction={FC_DIRECTION.HR}
						size={FC_SIZE.AUTO}
						position={FC_POSITION.AUTO}
						className={baseClass.toClassName()}						
						addArrow={false}
						offset={-2}
						safePadding={10}
						updateInterval={0}
						onClose={onClose}
					>
						<FloatingContainerContent
							className={baseClass.element("content").toClassName()}
							onClick={preventClick}
						>
							<ResourceList
								onSelect={handleSelect}
								selectedId={selectedId}
								style={RESOURCE_LIST_STYLE.COMPACT}
								isActive={isOpened}
								isSelectable={isSelectable}
								showRoot={basePath === ""}
								basePath={basePath}
								defaultExpandAll={false}
							/>
						</FloatingContainerContent>
					</FloatingContainer>
				);
			}}
		/>
	);
};
